import React, { useEffect, useRef, useState } from "react";
import { api, API_URL, asArray, getToken } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
    Check, Copy, Link as LinkIcon, Loader2, Upload, FileText, Image as ImageIcon,
    Trash2, Download, FileBox, Pencil, Search, Eye, BarChart3,
} from "lucide-react";

const formatBytes = (b) => {
    if (!b) return "0 B";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

export const FilesAdmin = () => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [kindFilter, setKindFilter] = useState("all");
    const [search, setSearch] = useState("");
    const inputRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (kindFilter !== "all") params.kind = kindFilter;
            const { data } = await api.get("/admin/attachments", { params });
            setFiles(asArray(data));
        } catch (err) {
            // 401 is handled globally by the auth interceptor — don't double-toast.
            if (err?.response?.status !== 401) {
                const detail = err?.response?.data?.detail
                    || err?.message
                    || "Erro desconhecido";
                toast.error(`Erro a carregar ficheiros: ${detail}`);
                if (process.env.NODE_ENV !== "production") {
                    console.warn("Failed to load attachments:", err);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [kindFilter]);

    const onPick = () => inputRef.current?.click();

    const onUpload = async (e) => {
        const fileList = Array.from(e.target.files || []);
        if (!fileList.length) return;
        setUploading(true);
        let ok = 0, fail = 0;
        for (const f of fileList) {
            try {
                const fd = new FormData();
                fd.append("file", f);
                await api.post("/admin/attachments", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                ok++;
            } catch (err) {
                fail++;
                const msg = err?.response?.data?.detail || `Falha em ${f.name}`;
                toast.error(msg);
            }
        }
        if (ok > 0) toast.success(`${ok} ficheiro(s) carregado(s)`);
        setUploading(false);
        if (inputRef.current) inputRef.current.value = "";
        load();
    };

    const onDelete = async (id, name) => {
        if (!window.confirm(`Apagar "${name}"? Esta ação não pode ser desfeita.`)) return;
        try {
            await api.delete(`/admin/attachments/${id}`);
            toast.success("Ficheiro apagado");
            setFiles((arr) => arr.filter((x) => x.id !== id));
        } catch {
            toast.error("Erro a apagar ficheiro");
        }
    };

    const onDownload = async (id, name) => {
        try {
            const token = getToken();
            const resp = await fetch(`${API_URL}/admin/attachments/${id}/download`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!resp.ok) throw new Error("download_failed");
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = name || "ficheiro";
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Erro a descarregar ficheiro");
        }
    };

    const filtered = files.filter((f) =>
        !search.trim() || (f.filename || "").toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="space-y-6" data-testid="files-admin">
            {/* Header / actions */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h2 className="font-serif text-2xl text-stone-900">Arquivo de Ficheiros</h2>
                    <p className="text-sm text-stone-500">PDFs e imagens para anexares aos envios.</p>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        accept="application/pdf,image/png,image/jpeg,image/webp,image/gif"
                        className="hidden"
                        onChange={onUpload}
                        data-testid="files-input"
                    />
                    <Button onClick={onPick} disabled={uploading} data-testid="files-upload-btn">
                        {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                        Carregar ficheiros
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white ring-1 ring-stone-200 rounded-2xl p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Procurar por nome..."
                        className="pl-9"
                        data-testid="files-search"
                    />
                </div>
                <div className="md:w-56">
                    <Select value={kindFilter} onValueChange={setKindFilter}>
                        <SelectTrigger data-testid="files-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tudo</SelectItem>
                            <SelectItem value="proposal_pdf">Apenas PDFs</SelectItem>
                            <SelectItem value="image">Apenas imagens</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Files grid */}
            {loading ? (
                <div className="flex items-center gap-2 text-stone-500 py-10">
                    <Loader2 className="h-4 w-4 animate-spin" /> A carregar…
                </div>
            ) : filtered.length === 0 ? (
                <div className="bg-stone-50 ring-1 ring-dashed ring-stone-300 rounded-2xl py-16 text-center">
                    <FileBox className="h-10 w-10 text-stone-400 mx-auto mb-3" />
                    <p className="text-stone-600">Ainda não há ficheiros no arquivo.</p>
                    <p className="text-sm text-stone-500 mt-1">Carrega o primeiro PDF ou imagem em cima.</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {filtered.map((f) => (
                        <FileCard
                            key={f.id}
                            file={f}
                            onDelete={() => onDelete(f.id, f.filename)}
                            onDownload={() => onDownload(f.id, f.filename)}
                            onSlugUpdated={(newSlug) => setFiles((arr) => arr.map(
                                (x) => (x.id === f.id ? { ...x, slug: newSlug } : x)
                            ))}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const FileCard = ({ file, onDelete, onDownload, onSlugUpdated }) => {
    const isImage = file.kind === "image";
    const [previewUrl, setPreviewUrl] = useState(null);
    const [slugEditing, setSlugEditing] = useState(false);
    const [slugValue, setSlugValue] = useState(file.slug || "");
    const [slugSaving, setSlugSaving] = useState(false);
    const [copied, setCopied] = useState(false);
    const [sharesOpen, setSharesOpen] = useState(false);

    const publicUrl = file.slug
        ? `${API_URL}/p/${file.slug}`
        : (file.public_token ? `${API_URL}/attachments/public/${file.public_token}` : "");

    const copyLink = async () => {
        if (!publicUrl) return;
        try {
            await navigator.clipboard.writeText(publicUrl);
            setCopied(true);
            toast.success("Link copiado.");
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            toast.error("Não foi possível copiar.");
        }
    };

    const saveSlug = async () => {
        const v = slugValue.trim();
        if (!v) {
            toast.error("O link não pode ficar vazio.");
            return;
        }
        setSlugSaving(true);
        try {
            const { data } = await api.patch(`/admin/attachments/${file.id}/slug`, { slug: v });
            onSlugUpdated?.(data.slug);
            setSlugValue(data.slug);
            setSlugEditing(false);
            toast.success("Link atualizado.");
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message;
            toast.error(`Erro: ${detail}`);
        } finally {
            setSlugSaving(false);
        }
    };

    useEffect(() => {
        let canceled = false;
        if (!isImage) return;
        (async () => {
            try {
                const token = getToken();
                const resp = await fetch(`${API_URL}/admin/attachments/${file.id}/download`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!resp.ok) return;
                const blob = await resp.blob();
                if (canceled) return;
                setPreviewUrl(URL.createObjectURL(blob));
            } catch (err) {
                if (process.env.NODE_ENV !== "production") {
                    console.warn("Failed to load image preview:", err?.message || err);
                }
            }
        })();
        return () => {
            canceled = true;
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [file.id, isImage]);

    return (
        <div
            className="group bg-white ring-1 ring-stone-200 rounded-2xl overflow-hidden flex flex-col"
            data-testid={`file-card-${file.id}`}
        >
            <div className="aspect-[4/3] bg-stone-100 flex items-center justify-center relative overflow-hidden">
                {isImage && previewUrl ? (
                    <img src={previewUrl} alt={file.filename} className="w-full h-full object-cover" />
                ) : isImage ? (
                    <ImageIcon className="h-10 w-10 text-stone-400" />
                ) : (
                    <FileText className="h-10 w-10 text-rose-700/70" />
                )}
                <Badge className={`absolute top-2 right-2 text-[10px] ${isImage ? "bg-emerald-100 text-emerald-900" : "bg-rose-100 text-rose-900"}`}>
                    {isImage ? "Imagem" : "PDF"}
                </Badge>
            </div>
            <div className="p-3 flex-1 flex flex-col">
                <div className="text-sm font-medium text-stone-800 truncate" title={file.filename}>
                    {file.filename}
                </div>
                <div className="text-xs text-stone-500 mt-1">{formatBytes(file.size_bytes)}</div>

                {/* Public link section */}
                <div className="mt-3 bg-stone-50 ring-1 ring-stone-200 rounded-lg p-2">
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-stone-500 mb-1">
                        <LinkIcon size={10} /> Link partilhável
                    </div>
                    {slugEditing ? (
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-stone-400 shrink-0">/p/</span>
                            <Input
                                value={slugValue}
                                onChange={(e) => setSlugValue(e.target.value)}
                                className="h-7 text-xs"
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveSlug(); } }}
                                autoFocus
                                data-testid={`slug-input-${file.id}`}
                            />
                            <Button
                                size="icon"
                                onClick={saveSlug}
                                disabled={slugSaving}
                                className="h-7 w-7 rounded-md bg-stone-900 text-white shrink-0"
                                data-testid={`slug-save-${file.id}`}
                            >
                                {slugSaving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-1">
                            <span className="flex-1 text-xs text-stone-700 truncate font-mono" title={publicUrl}>
                                /p/{file.slug || "—"}
                            </span>
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => { setSlugValue(file.slug || ""); setSlugEditing(true); }}
                                className="h-7 w-7 shrink-0"
                                aria-label="Editar link"
                                data-testid={`slug-edit-${file.id}`}
                            >
                                <Pencil size={12} />
                            </Button>
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={copyLink}
                                disabled={!publicUrl}
                                className="h-7 w-7 shrink-0"
                                aria-label="Copiar link"
                                data-testid={`slug-copy-${file.id}`}
                            >
                                {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                            </Button>
                        </div>
                    )}
                </div>

                <div className="mt-3 flex items-center gap-2">
                    <Button size="sm" variant="outline" className="flex-1" onClick={onDownload}>
                        <Download className="h-3.5 w-3.5 mr-1.5" /> Download
                    </Button>
                    {!isImage && (
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSharesOpen(true)}
                            className="text-stone-600 hover:bg-stone-100"
                            aria-label="Ver aberturas"
                            data-testid={`file-shares-btn-${file.id}`}
                        >
                            <BarChart3 className="h-3.5 w-3.5" />
                        </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={onDelete} className="text-rose-600 hover:bg-rose-50">
                        <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </div>
            {!isImage && (
                <SharesDialog
                    open={sharesOpen}
                    onOpenChange={setSharesOpen}
                    file={file}
                />
            )}
        </div>
    );
};

export default FilesAdmin;

const formatDateTime = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("pt-PT", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
};

const SharesDialog = ({ open, onOpenChange, file }) => {
    const [loading, setLoading] = useState(false);
    const [shares, setShares] = useState([]);
    const [copiedSlug, setCopiedSlug] = useState(null);

    useEffect(() => {
        if (!open) return;
        let canceled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/admin/attachments/${file.id}/shares`);
                if (!canceled) setShares(asArray(data));
            } catch (err) {
                if (!canceled) {
                    const detail = err?.response?.data?.detail || err?.message || "Erro";
                    toast.error(`Erro a carregar aberturas: ${detail}`);
                }
            } finally {
                if (!canceled) setLoading(false);
            }
        })();
        return () => { canceled = true; };
    }, [open, file.id]);

    const copyShareLink = async (slug) => {
        const url = `${API_URL}/p/${slug}`;
        try {
            await navigator.clipboard.writeText(url);
            setCopiedSlug(slug);
            toast.success("Link copiado.");
            setTimeout(() => setCopiedSlug(null), 1800);
        } catch {
            toast.error("Não foi possível copiar.");
        }
    };

    const totalOpens = shares.reduce((sum, s) => sum + (s.opened_count || 0), 0);
    const uniqueLeads = shares.length;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl max-h-[85vh] overflow-y-auto"
                data-testid={`shares-dialog-${file.id}`}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-serif text-xl text-stone-900">
                        <Eye className="h-5 w-5 text-rose-700" />
                        Aberturas — {file.filename}
                    </DialogTitle>
                    <DialogDescription>
                        Histórico de leads que abriram este ficheiro através dos links personalizados.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-stone-50 ring-1 ring-stone-200 rounded-xl p-3">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-stone-500">
                            Leads com link
                        </div>
                        <div
                            className="text-2xl font-serif text-stone-900 mt-1"
                            data-testid={`shares-unique-leads-${file.id}`}
                        >
                            {uniqueLeads}
                        </div>
                    </div>
                    <div className="bg-stone-50 ring-1 ring-stone-200 rounded-xl p-3">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-stone-500">
                            Total de aberturas
                        </div>
                        <div
                            className="text-2xl font-serif text-stone-900 mt-1"
                            data-testid={`shares-total-opens-${file.id}`}
                        >
                            {totalOpens}
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center gap-2 text-stone-500 py-8 justify-center">
                        <Loader2 className="h-4 w-4 animate-spin" /> A carregar…
                    </div>
                ) : shares.length === 0 ? (
                    <div className="bg-stone-50 ring-1 ring-dashed ring-stone-300 rounded-xl py-10 text-center">
                        <LinkIcon className="h-8 w-8 text-stone-400 mx-auto mb-2" />
                        <p className="text-stone-600 text-sm">Ainda ninguém abriu um link personalizado deste ficheiro.</p>
                        <p className="text-xs text-stone-500 mt-1">
                            Envia uma proposta com este PDF para começares a rastrear aberturas.
                        </p>
                    </div>
                ) : (
                    <div className="ring-1 ring-stone-200 rounded-xl overflow-hidden">
                        <table
                            className="w-full text-sm"
                            data-testid={`shares-table-${file.id}`}
                        >
                            <thead className="bg-stone-50">
                                <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-stone-500">
                                    <th className="px-3 py-2">Lead</th>
                                    <th className="px-3 py-2 text-center">Aberturas</th>
                                    <th className="px-3 py-2">Última abertura</th>
                                    <th className="px-3 py-2">Criado</th>
                                    <th className="px-3 py-2 text-right">Link</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                                {shares.map((s) => (
                                    <tr
                                        key={s.id}
                                        className="hover:bg-stone-50/60"
                                        data-testid={`share-row-${s.slug}`}
                                    >
                                        <td className="px-3 py-2">
                                            <div className="font-medium text-stone-800">
                                                {s.lead_nome || <span className="italic text-stone-400">sem nome</span>}
                                            </div>
                                            {s.lead_email && (
                                                <div className="text-xs text-stone-500">{s.lead_email}</div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            <Badge
                                                className={
                                                    s.opened_count > 0
                                                        ? "bg-emerald-100 text-emerald-900"
                                                        : "bg-stone-100 text-stone-600"
                                                }
                                                data-testid={`share-opens-${s.slug}`}
                                            >
                                                {s.opened_count || 0}
                                            </Badge>
                                        </td>
                                        <td className="px-3 py-2 text-stone-600">
                                            {s.last_opened_at ? formatDateTime(s.last_opened_at) : <span className="text-stone-400">nunca</span>}
                                        </td>
                                        <td className="px-3 py-2 text-stone-500 text-xs">
                                            {formatDateTime(s.created_at)}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                onClick={() => copyShareLink(s.slug)}
                                                className="h-7 w-7"
                                                aria-label="Copiar link"
                                                data-testid={`share-copy-${s.slug}`}
                                            >
                                                {copiedSlug === s.slug ? (
                                                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                                                ) : (
                                                    <Copy className="h-3.5 w-3.5" />
                                                )}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
